import { useGlassLook } from '@/state/glass'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { trackUse } from '@/state/usage'
import { AgentsNow } from '@/components/AgentsNow'
import { useStore } from 'zustand'
import { engine } from '@/runtime'
import { useUi } from '@/state/ui'
import { useAccess } from '@/state/access'
import { api } from '@/runtime'
import { useAllProjects } from '@/db/hooks'
import { ActiveBar } from './ActiveBar'
import { Toasts } from './Toasts'
import { EmbedDrawer, PlayerBar } from './Player'
import { VoiceBar, VoiceIconButton } from './VoiceBar'
import { useAgentState, type AgentState } from '@/voice/agent'
import { useVoicePrefs } from '@/state/voicePrefs'
import { VoiceAgentProvider, useVoiceAgent } from '@/voice/agent'
import { APP_ROUTES, runVoiceCommand } from '@/voice/commands'
import { useVoiceToday } from '@/voice/today'
import { PomodoroBar } from './Pomodoro'
import { QuickAdd } from './QuickAdd'
import { GlobalMenus } from './GlobalMenus'
import { CommandPalette } from './CommandPalette'
import { SyncDot } from './SyncDot'
import { UpdateChip } from './UpdateChip'
import { cx } from './ui'
import {
  ChevronRight,
  LayoutGrid,
  Minimize2,
  ChevronLeft,
  Tag as TagIcon,
  Mic,
  MicOff,
  Ear,
  Plus,
  Network,
  Search as SearchIcon,
  Sparkles,
} from 'lucide-react'
import { PrimeMark, PrimeTile } from '@/components/PrimeMark'
import { labelStyle, tokens } from '@/lib/colour'
import { DEFAULT_APP_ICON, applyAppIcon } from '@/lib/appIcon'
import { Icon } from '@/lib/icons'
import { useKeyboard } from '@/hooks/useKeyboard'
import { useKeyboardOpen, useVisualViewportVars } from '@/hooks/useVisualViewport'
import {
  useCurrentWorkspace,
  useLayers,
  usePages,
  useResolvedAppearance,
  useSettings,
  useSuitesRead,
  useTasks,
  useWorkspaces,
} from '@/db/hooks'
import { paletteFor } from '@/lib/look'
import { resolveLayers } from '@/lib/themeLayers'
import { closeGlyphOf, logoOf, scrollIndicatorOf, themeMarks } from '@/lib/themeMarks'
import { NavLogo, ScrollIndicator } from '@/components/ThemeSlots'
import { homeSlugOf, useScope } from '@/state/scope'
import { hiddenLabel } from '@/lib/follow'
import {
  accentVarsFor,
  GLASS,
  horizonVars,
  isLightTheme,
  themeVars,
  THEME_DEFAULT_ACCENT,
  type Theme,
} from '@/lib/colour'
import { suiteOf } from '@/suites'
import { SuiteFlash, SuiteSwitcher, pickSuite, useSuiteAim } from './SuiteSwitcher'
import { SuiteInstallSheet } from './SuiteInstallSheet'
import { ALL } from '@/kinds/workspace/schema'
import { navActive, type NavItem } from '@/lib/nav'
import { PRIME_APP } from '@/lib/appMode'
import { useDocked } from '@/state/panel'
import { useAllRuns } from '@/db/hooks'
import { isLead } from '@/kinds/agentrun/components'
import { useNav } from '@/lib/useNav'
import { minuteOfDay, skyAt, type Resolved } from '@/lib/horizon'
import { HorizonEdge } from './Horizon'
import { StudioBackground } from './StudioBackground'
import { canDraw, studioModeOf, themeSpecToVars, type StudioDraw } from '@/lib/themeStudio'
import { needsHorizonPin, useDevice } from '@/state/appearance'
import { useNow } from '@/hooks/useNow'
import { guardSelection, swallowNextClick, tick } from '@/lib/haptics'
import { DOCK_SLOTS } from '@/os/layout'
import { useEditing, useLauncher, usePhoneApps, usePhoneLayout } from '@/os/phone'
import { Launcher } from '@/os/Launcher'
import { DockPicker } from '@/os/DockPicker'
import { useEffect, useRef, useState } from 'react'

/** The workspace title: tapping it opens the switcher (spec v3 §1.2). */
const TitleButton = ({
  icon,
  title,
  onClick,
  testId,
}: {
  icon: React.ReactNode
  title: string
  onClick: () => void
  testId: string
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2 font-semibold tracking-tight rounded-md px-2 min-h-9 hover:bg-panel text-accent"
    aria-label="Switch suite"
    data-testid={testId}
    data-suite-anchor=""
  >
    <span className="text-lg leading-none">{icon}</span>
    <span>{title}</span>
    <span className="text-xs text-faint">▾</span>
  </button>
)

const BarTab = ({
  item: n,
  active,
  editing = false,
  onEdit,
  onTap,
}: {
  item: NavItem
  active: boolean
  /** arranging the home screens: a tap picks what sits in this slot */
  editing?: boolean
  onEdit?: () => void
  /** every ordinary tap, before the link navigates (closing the launcher, back to the top) */
  onTap?: () => void
}) => (
  <Link
    to={n.to}
    data-nav-to={editing ? undefined : n.to}
    onClick={(e) => {
      if (!editing) {
        onTap?.()
        return
      }
      e.preventDefault()
      onEdit?.()
    }}
    className={cx(
      'relative z-10 flex flex-col items-center justify-center gap-0.5 h-full w-full min-w-12 text-[10.5px] font-medium transition-colors',
      active ? 'text-accent-text' : 'text-muted',
      editing && 'os-jiggle',
    )}
    data-active={active || undefined}
    data-testid={`tab-${n.key}`}
  >
    {n.Icon ? (
      <n.Icon size={21} strokeWidth={active ? 2.2 : 1.8} aria-hidden />
    ) : (
      <span className="text-base leading-5">{n.icon}</span>
    )}
    <span className="max-w-16 truncate">{n.short ?? n.label}</span>
  </Link>
)

/** The Prime app's tab bar (docs/prime-app.md): its thread, the Graph, every agent. */
const PRIME_DOCK: NavItem[] = [
  { key: 'prime', to: '/agents', label: 'Prime', icon: '✦', Icon: PrimeMark, section: 'more' },
  { key: 'graph', to: '/agents/graph', label: 'Graph', icon: '⌘', Icon: Network, section: 'more' },
  { key: 'agents', to: '/agents/all', label: 'Agents', icon: '✨', Icon: Sparkles, section: 'more' },
]

// variables only a studio theme adds on top of the stylesheet's defaults; dropped again when it goes
const STUDIO_ONLY =
  /^--(color-(sunken|highlight|highlight-text|ok|warn|info)|studio-.+|space-scale|type-scale|weight-body|icon-tint.*|radius-.+|glass-.+)$/

/** Put the look's variables on <html>, and take back those a studio theme set that the next look does not. */
const paintVars = (v: Record<string, string>) => {
  const root = document.documentElement
  for (const [k, val] of Object.entries(v)) {
    if (k === 'color-scheme') root.style.colorScheme = val
    else root.style.setProperty(k, val)
  }
  for (const k of (root.dataset.studioVars ?? '').split(' ')) if (k && !(k in v)) root.style.removeProperty(k)
  root.dataset.studioVars = Object.keys(v)
    .filter((k) => STUDIO_ONLY.test(k))
    .join(' ')
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', v['--color-ink'] ?? '#15142b')
}

const HOLD_MS = 225

const SCRUB_SLOP = 10

/**
 * Scrubbing the phone tab bar: press anywhere on it and slide, and the page
 * under your finger opens as you pass over it (like scrubbing the iOS keyboard).
 * Tabs carry data-nav-to; the centre button is skipped, and letting go on More
 * opens the sheet. Taps still work as taps.
 */
const useNavScrub = (navigate: (to: string) => void, openMore: () => void) => {
  const state = useRef<{ id: number; x: number; moved: boolean; last: string | null } | null>(null)
  const targetAt = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-nav-to],[data-nav-more]')
    if (!el) return null
    return el.dataset.navMore !== undefined ? 'more' : (el.dataset.navTo ?? null)
  }
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // no pointer capture: it would retarget the tap's click to the bar and break plain taps;
    // touch already delivers the whole gesture to the element it started on, which is inside the bar
    state.current = { id: e.pointerId, x: e.clientX, moved: false, last: null }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const st = state.current
    if (!st || st.id !== e.pointerId) return
    if (!st.moved && Math.abs(e.clientX - st.x) < SCRUB_SLOP) return
    st.moved = true
    const to = targetAt(e.clientX, e.clientY)
    if (!to || to === st.last) return
    st.last = to
    if (to !== 'more') {
      navigate(to)
      tick(5)
    }
  }
  const onPointerEnd = (e: React.PointerEvent) => {
    const st = state.current
    if (!st || st.id !== e.pointerId) return
    state.current = null
    if (st.moved && st.last === 'more') openMore()
  }
  return { onPointerDown, onPointerMove, onPointerUp: onPointerEnd, onPointerCancel: onPointerEnd }
}

/**
 * The raised centre button of the phone tab bar: the workspace's icon in the
 * accent circle. Tap adds (quick-add), press and hold switches workspace, so
 * the header no longer needs a title button and the floating + is gone. Held,
 * the finger can carry on onto a suite's card in the switcher and let go
 * there to switch in one gesture (`onSlidePick`); letting go anywhere else
 * leaves the switcher open.
 */
const CentreButton = ({
  icon,
  title,
  prime,
  onTap,
  onSwitch,
  onSlidePick,
}: {
  icon: React.ReactNode
  title: string
  /** a dev suite's centre is Prime's mark (tap opens Prime); everywhere else it is the plus (tap adds) */
  prime: boolean
  onTap: () => void
  onSwitch: () => void
  onSlidePick: (slug: string) => void
}) => {
  const agent = useAgentState()
  const live = agent.available && agent.status !== 'disconnected'
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null)
  const [held, setHeld] = useState(false)
  const [origin, setOrigin] = useState<{ x: number; y: number } | null>(null)
  // after the hold: where the finger was when the switcher opened, and whether it has set off since
  const slide = useRef<{ x: number; y: number; moved: boolean } | null>(null)
  const last = useRef({ x: 0, y: 0 })
  const setAim = useSuiteAim((s) => s.setAim)
  const down = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') guardSelection()
    setOrigin({ x: e.clientX, y: e.clientY })
    last.current = { x: e.clientX, y: e.clientY }
    slide.current = null
    setHeld(false)
    const el = e.currentTarget as HTMLElement
    const pointer = e.pointerId
    setTimer(
      setTimeout(() => {
        setHeld(true)
        setTimer(null)
        tick()
        // the switcher opens under the finger: keep the lifting finger's click off it
        swallowNextClick()
        // a touch stays with this button anyway; a mouse needs holding on to for the slide
        try {
          el.setPointerCapture(pointer)
        } catch {
          // the pointer has gone: no slide, the switcher still opens
        }
        slide.current = { ...last.current, moved: false }
        onSwitch()
      }, HOLD_MS),
    )
  }
  const cancel = () => {
    if (timer) clearTimeout(timer)
    setTimer(null)
  }
  // a touch keeps the pointer on the element it started on, so pointerleave never comes:
  // sliding off the ball must cancel the hold by distance (the bar's scrub takes over)
  const move = (e: React.PointerEvent) => {
    last.current = { x: e.clientX, y: e.clientY }
    const s = slide.current
    if (s) {
      // only once the finger sets off, so a card that happens to sit under it is not picked by lifting
      if (!s.moved && Math.hypot(e.clientX - s.x, e.clientY - s.y) < SCRUB_SLOP) return
      s.moved = true
      const over = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>('[data-suite]')
      const slug = over?.dataset.suite ?? null
      if (slug !== useSuiteAim.getState().aim) {
        setAim(slug)
        if (slug) tick(5)
      }
      return
    }
    if (timer && origin && Math.hypot(e.clientX - origin.x, e.clientY - origin.y) > SCRUB_SLOP) cancel()
  }
  const up = () => {
    cancel()
    const aim = useSuiteAim.getState().aim
    if (slide.current?.moved && aim) onSlidePick(aim)
    slide.current = null
    setAim(null)
  }
  const lost = () => {
    cancel()
    slide.current = null
    setAim(null)
  }
  if (live) return <LiveCentreButton agent={agent} />
  return (
    <button
      type="button"
      aria-label={`${prime ? 'Prime' : 'Add'} (hold to switch suite, now ${title})`}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerLeave={cancel}
      onPointerCancel={lost}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        if (!held) onTap()
        setHeld(false)
      }}
      className="relative z-10 mx-1.5 flex flex-col items-center select-none touch-none"
      style={{ WebkitTouchCallout: 'none' }}
      data-testid="fab"
    >
      {/* the plus (a dev suite: Prime's mark) is what a tap does; the workspace rides on the badge, since holding switches it */}
      {prime ? (
        <PrimeTile
          size={24}
          className="h-11 w-11 rounded-full border border-line flex items-center justify-center active:scale-95 transition-transform"
        />
      ) : (
        <span className="h-11 w-11 rounded-full bg-accent text-on-accent flex items-center justify-center active:scale-95 transition-transform shadow-[0_8px_20px_-10px_var(--color-accent)]">
          <Plus size={22} strokeWidth={2.4} aria-hidden />
        </span>
      )}
      <span
        className="absolute -right-1.5 top-6 h-5 w-5 rounded-full bg-panel border border-line text-fg text-[10px] leading-none flex items-center justify-center"
        aria-hidden
      >
        {icon}
      </span>
    </button>
  )
}

/**
 * The centre button while a conversation is on: the talk control, not the
 * line (the bar above hangs up). Push to talk: the mic is shut until the
 * button is held, so nothing in the room reaches the agent between turns, and
 * letting go is the reply's cue. Open mic: a tap says "your turn" and shuts
 * the mic until the reply begins. The ring breathes with whoever is talking.
 */
const LiveCentreButton = ({ agent }: { agent: AgentState }) => {
  const pushToTalk = useVoicePrefs((s) => s.pushToTalk)
  const connected = agent.status === 'connected'
  // push to talk holds the mic shut for the whole call on this surface, and
  // lets it go when the mode is switched off or the call ends
  useEffect(() => {
    if (!connected || !pushToTalk) return
    agent.setMuted(true)
    return () => agent.setMuted(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setMuted is stable per session
  }, [connected, pushToTalk])
  const level = agent.speaking ? agent.outLevel : agent.level
  const holding = pushToTalk && connected && !agent.muted
  const hold = (on: boolean) => {
    if (!pushToTalk || !connected) return
    agent.setMuted(!on)
    if (on) tick(8)
  }
  return (
    <button
      type="button"
      aria-label={pushToTalk ? 'Hold to talk' : agent.turnPending ? 'Waiting for the reply' : 'Your turn'}
      onPointerDown={() => hold(true)}
      onPointerUp={() => hold(false)}
      onPointerCancel={() => hold(false)}
      onPointerLeave={() => hold(false)}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        if (!pushToTalk && connected && !agent.turnPending) agent.yourTurn()
      }}
      className="relative z-10 mx-1.5 flex flex-col items-center select-none touch-none"
      style={{ WebkitTouchCallout: 'none' }}
      data-testid="fab"
      data-live={pushToTalk ? 'hold' : 'turn'}
    >
      <span
        className="absolute top-0 h-11 w-11 rounded-full bg-accent/35 transition-transform duration-150"
        style={{ transform: `scale(${1 + Math.min(1, level) * 0.5})` }}
        aria-hidden
      />
      <span
        className={cx(
          'relative h-11 w-11 rounded-full flex items-center justify-center transition-transform shadow-[0_8px_20px_-10px_var(--color-accent)]',
          holding ? 'bg-accent text-on-accent scale-110' : 'bg-accent text-on-accent',
          !pushToTalk && 'active:scale-95',
          agent.status === 'connecting' && 'animate-pulse',
          agent.muted && !holding && 'bg-raised text-fg border border-line',
        )}
      >
        {agent.muted && !holding ? (
          <MicOff size={24} />
        ) : !pushToTalk && agent.speaking ? (
          <Ear size={24} />
        ) : (
          <Mic size={24} />
        )}
      </span>
    </button>
  )
}

/** How much of the sky each appearance lets through the top of the page. */
const SKY_STRENGTH: Record<Resolved, string> = {
  day: '34%',
  night: '42%',
  black: '0%',
  glass: '0%',
  workspace: '12%',
}

/** Sidebar on desktop, bottom tab bar under 768px. No counts anywhere. */
/**
 * The app inside the spoken agent's provider,
 * mounted once here so a conversation follows you from page to page like the
 * player does.
 */
export const Shell = () => (
  <VoiceAgentProvider>
    <ShellInner />
  </VoiceAgentProvider>
)

/** Mounts the agent with the app's handlers: open navigates, everything else is the shared runner. */
const ShellVoice = () => {
  const navigate = useNavigate()
  const today = useVoiceToday()
  const projects = useAllProjects() ?? []
  const lastSaid = useRef('')
  const say = (text: string) => {
    lastSaid.current = text
    useUi.getState().showFlash(text, 2500)
  }
  useVoiceAgent({
    run: async (c) => {
      lastSaid.current = ''
      await runVoiceCommand(c, {
        say,
        today,
        projects,
        open: (view) => {
          // "apps" is the launcher, an overlay rather than a page
          if (view === 'apps') {
            useLauncher.getState().setOpen(true)
            return true
          }
          const route = APP_ROUTES[view]
          if (!route) return false
          void navigate({ to: route })
          return true
        },
      })
      return lastSaid.current || 'done'
    },
    home: async (name, service = 'toggle') => {
      const r = await api.homeStates([])
      const n = name.trim().toLowerCase()
      const hit = r.entities
        .filter((e) => e.toggle)
        .find((e) => e.name.toLowerCase().includes(n) || e.id.toLowerCase().includes(n.replace(/\s+/g, '_')))
      if (!hit) return `No light or switch called ${name}.`
      await api.homeCall(hit.id, service)
      return `${service.replace('_', ' ')} ${hit.name}`
    },
  })
  return null
}

const ShellInner = () => {
  const path = useRouterState({ select: (s) => s.location.pathname })
  const member = useAccess((s) => s.member)
  // which screen is open, as its route pattern (never the ids in the path): first-party usage
  const routeId = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.routeId as string | undefined,
  })
  useEffect(() => {
    if (routeId && routeId !== '__root__') trackUse(`screen:${routeId}`)
  }, [routeId])
  const setQuickAdd = useUi((s) => s.setQuickAdd)
  const sync = useStore(engine.store)
  const mismatch = engine.dayMismatch()
  useKeyboard()
  useTasks() // keep the resources live query warm so navigation is instant
  const scope = useScope((s) => s.ws)
  const current = useCurrentWorkspace()
  // the dev suite is installed in the active workspace (Everything has no suite, so it keeps the plus)
  const devSuite = scope !== ALL && suiteOf(current).id === 'devos'
  const suites = useWorkspaces()
  const setScope = useScope((s) => s.setWs)
  // the suite this device is in was removed (here, by undoing its install, or on
  // another device): back to the home suite (the default, or a member's own) rather than pointing at nothing
  useEffect(() => {
    if (suites.length === 0) return
    const live = suites
      .filter((w) => !w.deleted_at && !(w.status as { archived_at?: string }).archived_at)
      .map((w) => w.spec)
    const home = homeSlugOf(live)
    if (useScope.getState().home !== home) useScope.setState({ home })
    if (scope !== ALL && !live.some((w) => w.slug === scope)) setScope(home)
  }, [scope, suites, setScope])
  const switcherOpen = useUi((s) => s.switcherOpen)
  const setSwitcher = useUi((s) => s.setSwitcher)
  const [installing, setInstalling] = useState(false)
  const collapsed = useUi((s) => s.sidebarCollapsed)
  const toggleSidebar = useUi((s) => s.toggleSidebar)
  const focusMode = useUi((s) => s.focusMode)
  // a docked panel (artifacts, Prime beside a page) gives the page the whole width
  const docked = useDocked((s) => s.on)
  const toggleFocus = useUi((s) => s.toggleFocus)
  const setRightRail = useUi((s) => s.setRightRail)
  const navCollapsed = useUi((s) => s.navCollapsed)
  const pagesQ = usePages()
  const tasksQ = useTasks()
  const settings = useSettings()
  useVisualViewportVars()
  // share-target / deep link: ?text= (from the share sheet or a Shortcut) opens quick-add with it filled in
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const shared = [params.get('title'), params.get('text'), params.get('url')]
      .filter(Boolean)
      .join(' ')
      .trim()
    if (!shared) return
    useUi.getState().setQuickAdd(true, shared)
    window.history.replaceState({}, '', window.location.pathname)
  }, [])
  const toggleNavSection = useUi((s) => s.toggleNavSection)
  const setPalette = useUi((s) => s.setPalette)
  const title = scope === ALL ? 'Everything' : (current?.spec.name ?? 'AlfieOS')
  // the app's own mark is the Everything ✦ and the home suite's default ◉ (or no icon); a theme's logo slot
  // replaces those, never a suite's own emoji
  const ownMark = !current?.spec.icon || current.spec.icon === '◉'
  const icon =
    scope === ALL || ownMark ? (
      <NavLogo>{scope === ALL ? '✦' : '◉'}</NavLogo>
    ) : (
      <Icon value={current?.spec.icon} size={18} fallback="◉" />
    )
  // the centre button's badge: small, and a workspace with no icon shows its initial rather than a dot
  const badge =
    scope === ALL || ownMark ? (
      <NavLogo size={13}>
        {scope === ALL ? '✦' : current?.spec.icon ? '◉' : title.slice(0, 1).toUpperCase()}
      </NavLogo>
    ) : (
      <Icon value={current?.spec.icon} size={13} fallback={title.slice(0, 1).toUpperCase()} />
    )
  const lead = (useAllRuns() ?? []).find((r) => isLead(r) && !r.deleted_at)
  // Prime's thread lights Prime, every other agent Agents, the Graph the Graph (lib/nav.ts agentsApp)
  const isActive = (to: string, exact = false) => navActive(to, path, exact, lead?.id)
  const { sections, coreNav } = useNav()
  // the phone's arrangement: the dock's three chosen slots, and the launcher behind the Apps tab
  const phone = usePhoneLayout()
  const { byId: appById } = usePhoneApps()
  const editingHome = useEditing((s) => s.home)
  const [dockSlot, setDockSlot] = useState<number | null>(null)
  // a slot whose app this workspace does not have falls back through the workspace's own pages
  const dock: NavItem[] = PRIME_APP ? [...PRIME_DOCK] : []
  for (const id of PRIME_APP ? [] : [...phone.dock, 'home', ...coreNav.map((n) => n.key)]) {
    const app = appById.get(id)
    if (app && !dock.includes(app) && dock.length < DOCK_SLOTS) dock.push(app)
  }
  const launcherOpen = useLauncher((s) => s.open)
  const setLauncher = useLauncher((s) => s.setOpen)
  const appsActive = launcherOpen || !dock.some((n) => isActive(n.to, n.exact))
  const keyboardOpen = useKeyboardOpen()
  const navigate = useNavigate()
  const scrub = useNavScrub(
    (to) => {
      setLauncher(false)
      void navigate({ to })
    },
    () => setLauncher(true),
  )
  // leaving a page closes the launcher, however it was left
  useEffect(() => setLauncher(false), [path, setLauncher])
  // A tab tap always closes the launcher: tapping the tab of the page already
  // under it changes no route, so the effect above never runs and the tap would
  // do nothing. With the launcher shut, the current tab takes the page to the top.
  const tabTap = (n: NavItem) => {
    if (launcherOpen) setLauncher(false)
    else if (isActive(n.to, n.exact)) window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  // the tab bar's pill: measured from the tab that says it is current and moved
  // there by style, so a tap slides it across rather than re-rendering the bar
  const navRef = useRef<HTMLElement>(null)
  const pillRef = useRef<HTMLSpanElement>(null)
  const dockKey = dock.map((n) => n.key).join()
  // which tab is lit, so the pill follows it when that changes without a route change
  // (Prime's thread is known as Prime's only once the runs have loaded)
  const litKey = appsActive ? 'apps' : (dock.find((n) => isActive(n.to, n.exact))?.key ?? '')
  useEffect(() => {
    const place = () => {
      const nav = navRef.current
      const pill = pillRef.current
      if (!nav || !pill) return
      const on = nav.querySelector<HTMLElement>('[data-active]')
      if (!on) {
        pill.style.opacity = '0'
        return
      }
      const n = nav.getBoundingClientRect()
      const r = on.getBoundingClientRect()
      const inset = 4
      pill.style.width = `${r.width - inset * 2}px`
      pill.style.transform = `translateX(${r.left - n.left + inset}px)`
      pill.style.opacity = '1'
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [path, launcherOpen, dockKey, keyboardOpen, litKey])
  const layers = useLayers()
  const rl = resolveLayers(layers)
  const theme = (rl.theme.kind === 'palette' ? rl.theme.theme : undefined) as Theme | undefined
  const palette = rl.theme.kind === 'palette' ? paletteFor(rl.theme.theme, rl.theme.palette) : rl.palette
  const hue =
    rl.colour ??
    (theme && theme in THEME_DEFAULT_ACCENT
      ? THEME_DEFAULT_ACCENT[theme as keyof typeof THEME_DEFAULT_ACCENT]
      : undefined)
  // the look's brightness and theme, from the device over the person over the workspace
  // (lib/themeLayers.ts): a palette is `workspace` here, the others are the clock's day or night,
  // black or glass
  const resolved = useResolvedAppearance()
  const glassLook = useGlassLook((s) => s.look)
  // a device carried over from the old mode: pin Horizon when the other layers would now show a palette
  const devicePending = useDevice((s) => s.pending)
  const settleDevice = useDevice((s) => s.settle)
  const suitesRead = useSuitesRead()
  useEffect(() => {
    if (devicePending && suitesRead)
      settleDevice(needsHorizonPin({ person: layers.person, workspace: layers.workspace }))
  }, [devicePending, suitesRead, settleDevice, layers.person, layers.workspace])
  const skyNow = useNow(60_000)
  // the studio's applied theme (themes studio) wins over the older look for the surfaces, in the
  // device's mode; the suite's own palette ("Suite theme") is what the studio is not
  const studio = rl.theme.kind === 'studio' ? rl.theme : undefined
  const studioSpec = studio && canDraw(studio.spec) ? studio.spec : undefined
  const draw: StudioDraw | undefined =
    studioSpec && resolved !== 'workspace'
      ? themeSpecToVars(
          resolved === 'glass' && !studioSpec.glass
            ? {
                ...studioSpec,
                glass: {
                  on: true,
                  strength: Math.round((glassLook.glow / GLASS.glowMax) * 100),
                  blur: glassLook.blur,
                },
              }
            : studioSpec,
          studioModeOf(resolved),
          { palette, hue },
        )
      : undefined
  const appearance = draw?.glass ? 'glass' : resolved
  const vars: Record<string, string> = {
    ...(draw
      ? draw.vars
      : resolved === 'workspace'
        ? { ...themeVars(theme, palette), ...accentVarsFor(palette, hue, isLightTheme(theme, palette)) }
        : horizonVars(resolved, palette, hue, glassLook)),
    '--sky': skyAt(minuteOfDay(skyNow)),
    '--sky-strength': SKY_STRENGTH[resolved],
  }
  // the palette lives on <html> so body text, scrollbars and sheets outside the grid follow it too
  const varsKey = JSON.stringify(vars)
  // color-scheme is set on <html> below; React's style prop does not know it
  const styleVars = Object.fromEntries(Object.entries(vars).filter(([k]) => k !== 'color-scheme'))
  useEffect(() => paintVars(JSON.parse(varsKey) as Record<string, string>), [varsKey])
  const attrsKey = JSON.stringify(draw?.attrs ?? {})
  useEffect(() => {
    const a = JSON.parse(attrsKey) as StudioDraw['attrs']
    for (const k of ['motion', 'space', 'type'] as const) {
      if (a[k]) document.documentElement.setAttribute(`data-${k}`, a[k])
      else document.documentElement.removeAttribute(`data-${k}`)
    }
  }, [attrsKey])
  useEffect(() => {
    document.documentElement.dataset.appearance = appearance
  }, [appearance])
  // the theme in force, marked on <html> (CSS and CloseGlyph read it instead of guessing from colours)
  const marksKey = JSON.stringify({
    ...themeMarks(rl),
    glyph: closeGlyphOf(rl),
    logo: logoOf(rl),
    scroll: scrollIndicatorOf(rl),
  })
  useEffect(() => {
    const m = JSON.parse(marksKey) as ReturnType<typeof themeMarks> & {
      glyph: string
      logo: string
      scroll: string
    }
    const root = document.documentElement
    root.setAttribute('data-theme-kind', m.kind)
    root.setAttribute('data-close-glyph', m.glyph)
    root.setAttribute('data-logo', m.logo)
    root.setAttribute('data-scroll', m.scroll)
    for (const [attr, v] of [
      ['data-palette', m.palette],
      ['data-studio-theme', m.studio],
    ] as const) {
      if (v) root.setAttribute(attr, v)
      else root.removeAttribute(attr)
    }
  }, [marksKey])
  // home-screen icon follows Settings; drawn on the device
  const appIcon = settings.app_icon
  const appIconKey = JSON.stringify(appIcon ?? DEFAULT_APP_ICON)
  useEffect(() => {
    // the Prime app keeps its own icon (prime.html, docs/prime-app.md)
    if (PRIME_APP) return
    void applyAppIcon(JSON.parse(appIconKey), (current?.spec.name ?? 'A').slice(0, 1).toUpperCase())
  }, [appIconKey, current?.spec.name])
  // Labels section: the workspace's most used key:value labels, with their colours
  const labelCounts = new Map<string, number>()
  for (const r of [...(tasksQ ?? []), ...(pagesQ ?? [])]) {
    if (r.deleted_at) continue
    for (const [k, v] of Object.entries(r.labels))
      // the system's own labels (posted.*, via, added_by, nudges) are never listed
      if (!hiddenLabel(k)) labelCounts.set(`${k}:${v}`, (labelCounts.get(`${k}:${v}`) ?? 0) + 1)
  }
  const topLabels = [...labelCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 8)
    .map(([kv]) => {
      const [k, ...rest] = kv.split(':')
      const v = rest.join(':')
      const st = labelStyle(k!, v, settings.label_styles)
      return { kv, k: k!, v, colour: tokens(st.hue).stripe, icon: st.icon }
    })
  return (
    <div
      className={cx(
        'min-h-dvh md:grid',
        focusMode
          ? 'md:grid-cols-[1fr]'
          : collapsed
            ? 'md:grid-cols-[3.5rem_1fr_auto]'
            : 'md:grid-cols-[13rem_1fr_auto]',
      )}
      style={styleVars as React.CSSProperties}
      data-focus={focusMode}
    >
      {draw && <StudioBackground draw={draw.background} />}
      {!focusMode && (
        <aside
          // above Home's wallpaper, which is fixed over the whole window behind the page
          className={cx(
            'hidden md:flex flex-col sticky top-0 z-10 h-dvh',
            collapsed ? 'p-2 items-center' : 'px-2 py-3',
          )}
          data-testid="sidebar"
          data-collapsed={collapsed}
        >
          {/* the sidebar's edge is its toggle: a hairline lights up on hover with a handle
              saying which way it will go, and a click folds or opens it (⌘\\ too) */}
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand (⌘\\)' : 'Collapse (⌘\\)'}
            className={cx(
              'group/edge absolute inset-y-0 -right-2 z-20 w-4 flex items-center justify-center outline-none',
              collapsed ? 'cursor-e-resize' : 'cursor-w-resize',
            )}
            data-testid="sidebar-toggle"
          >
            <span
              className="absolute inset-y-3 left-1/2 w-px -translate-x-1/2 rounded-full bg-transparent transition-colors duration-150 group-hover/edge:bg-accent/70 group-focus-visible/edge:bg-accent"
              aria-hidden
            />
            <span
              className="relative h-6 w-6 rounded-full border border-line bg-panel text-muted flex items-center justify-center opacity-0 scale-75 transition-[opacity,transform] duration-150 group-hover/edge:opacity-100 group-hover/edge:scale-100 group-focus-visible/edge:opacity-100 group-focus-visible/edge:scale-100 shadow-[var(--shadow-pop)]"
              aria-hidden
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </span>
          </button>
          {collapsed ? (
            <>
              <button
                type="button"
                onClick={() => setSwitcher(true)}
                className="h-9 w-9 rounded-md text-lg hover:bg-panel"
                aria-label="Switch suite"
                title={title}
                data-suite-anchor=""
              >
                {icon}
              </button>
              <div className="mb-2" />
            </>
          ) : (
            <div className="flex items-center justify-between mb-3 pl-1">
              <TitleButton
                icon={icon}
                title={title}
                onClick={() => setSwitcher(true)}
                testId="workspace-title-desktop"
              />
              <div className="flex items-center gap-1.5 pr-1">
                <VoiceIconButton className="h-7 w-7 !p-0 rounded-md text-faint hover:bg-panel hover:text-fg" />
                <SyncDot />
              </div>
            </div>
          )}
          <nav
            // its own scroll above the pinned Search and New: the bottom edge fades whatever
            // is under it, and the padding under the last entry clears the fade at the end,
            // so a cut-off row reads as "scrolls", never as half a row
            className={cx(
              'flex-1 flex flex-col overflow-y-auto overscroll-contain min-h-0 pb-6 scrollbar-none',
              '[mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)]',
              collapsed ? 'items-center gap-0.5' : 'gap-3',
            )}
            aria-label="Pages"
            data-testid="sidebar-nav"
          >
            {sections.map((sec, si) => {
              const folded = !collapsed && !!navCollapsed[sec.key]
              // Search is the box under the list here (⌘K), so it is not a row as well
              const all = sec.items.filter((n) => n.key !== 'search')
              // a folded section still shows the page you are on, so you never lose your place
              const items = folded ? all.filter((n) => isActive(n.to, n.exact)) : all
              return (
                <div
                  key={sec.key}
                  className={cx('flex flex-col gap-px', collapsed && 'items-center')}
                  data-testid={`nav-section-${sec.key}`}
                  data-folded={folded}
                >
                  {collapsed ? (
                    si > 0 && <div className="my-1 h-px w-6 bg-line" aria-hidden />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleNavSection(sec.key)}
                      aria-expanded={!folded}
                      className="flex w-full items-center h-7 px-2 mb-0.5 rounded-md text-xs font-medium text-muted hover:text-fg select-none"
                    >
                      <span className="flex-1 text-left">{sec.label}</span>
                      <ChevronRight
                        size={14}
                        className={cx('transition-transform', !folded && 'rotate-90')}
                        aria-hidden
                      />
                    </button>
                  )}
                  {items.map((n) => {
                    const active = isActive(n.to, n.exact)
                    return (
                      <Link
                        key={n.to}
                        to={n.to}
                        title={n.label}
                        // hover is fainter than the current page, so a pointer left over one
                        // entry never reads as a second page being open
                        className={cx(
                          'rounded-md text-sm flex items-center gap-2.5 transition-colors',
                          collapsed ? 'h-9 w-9 justify-center' : 'h-8 px-2',
                          active ? 'bg-raised text-fg font-medium' : 'hover:bg-raised/45',
                        )}
                        aria-current={active ? 'page' : undefined}
                      >
                        {n.key === 'prime' ? (
                          // Prime keeps its own colours in the sidebar, like its app icon
                          <PrimeTile
                            size={13}
                            className="h-[18px] w-[18px] -mx-px rounded-[5px] shrink-0 flex items-center justify-center"
                          />
                        ) : n.Icon ? (
                          <n.Icon
                            size={16}
                            className={cx('shrink-0', active ? 'text-accent' : 'text-muted')}
                            aria-hidden
                          />
                        ) : (
                          <span
                            className={cx(
                              'text-base leading-none w-4 text-center shrink-0',
                              active ? 'text-accent' : 'text-muted',
                            )}
                          >
                            {n.icon}
                          </span>
                        )}
                        {!collapsed && <span className="truncate">{n.label}</span>}
                      </Link>
                    )
                  })}
                </div>
              )
            })}
            <AgentsNow collapsed={collapsed} />
            {!collapsed && topLabels.length > 0 && (
              <div
                className="flex flex-col gap-px"
                data-testid="nav-section-labels"
                data-folded={!!navCollapsed.labels}
              >
                <button
                  type="button"
                  onClick={() => toggleNavSection('labels')}
                  aria-expanded={!navCollapsed.labels}
                  className="flex w-full items-center h-7 px-2 mb-0.5 rounded-md text-xs font-medium text-muted hover:text-fg select-none"
                >
                  <span className="flex-1 text-left">Labels</span>
                  <ChevronRight
                    size={14}
                    className={cx('transition-transform', !navCollapsed.labels && 'rotate-90')}
                    aria-hidden
                  />
                </button>
                {!navCollapsed.labels &&
                  topLabels.map((l) => (
                    <Link
                      key={l.kv}
                      to="/search"
                      search={{ q: l.kv }}
                      className="rounded-md text-sm flex items-center gap-2.5 h-8 px-2 hover:bg-raised"
                      title={l.kv}
                    >
                      {l.icon ? (
                        <span className="w-4 text-center text-sm leading-none">{l.icon}</span>
                      ) : (
                        <span
                          className="inline-block h-2.5 w-2.5 mx-[3px] rounded-full shrink-0"
                          style={{ background: l.colour }}
                          aria-hidden
                        />
                      )}
                      <span className="truncate">
                        <span className="text-muted">{l.k}:</span>
                        {l.v}
                      </span>
                    </Link>
                  ))}
                {!navCollapsed.labels && (
                  <Link
                    to="/settings/$section"
                    params={{ section: 'appearance' }}
                    className="rounded-md text-sm flex items-center gap-2.5 h-8 px-2 text-muted hover:bg-raised hover:text-fg"
                  >
                    <TagIcon size={14} className="mx-px" aria-hidden />
                    <span>All labels</span>
                  </Link>
                )}
              </div>
            )}
          </nav>
          <button
            type="button"
            onClick={() => setPalette(true)}
            title="Search everything (⌘K)"
            className={cx(
              'mt-2 shrink-0 rounded-md border border-line text-muted hover:bg-panel text-sm flex items-center gap-2',
              collapsed ? 'h-9 w-9 justify-center' : 'px-3 py-1.5',
            )}
            aria-label="Search everything"
            data-testid="sidebar-search"
          >
            <SearchIcon size={14} aria-hidden />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Search</span>
                <kbd className="text-faint">⌘K</kbd>
              </>
            )}
          </button>
          <div className={cx('shrink-0 pt-2', collapsed ? 'flex flex-col items-center gap-2' : '')}>
            <button
              type="button"
              onClick={() => setQuickAdd(true)}
              className={cx(
                'rounded-md bg-accent text-on-accent font-medium text-sm flex items-center justify-center gap-1.5',
                collapsed ? 'h-9 w-9' : 'w-full py-2 px-3',
              )}
              title="New (N)"
              aria-label="New"
            >
              <Plus size={collapsed ? 18 : 15} strokeWidth={2.4} aria-hidden />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">New</span>
                  <kbd className="border-on-accent/35 text-on-accent/75 font-normal">N</kbd>
                </>
              )}
            </button>
            {collapsed && <SyncDot />}
          </div>
        </aside>
      )}

      <div
        className={cx(
          'flex flex-col min-h-dvh',
          // desktop: an inset panel with rounded corners, like Cortex's layout
          !focusMode &&
            'md:min-h-[calc(100dvh-1rem)] md:my-2 md:mr-2 md:rounded-2xl md:border md:border-line/70 md:bg-panel/35 md:overflow-clip',
        )}
      >
        <ShellVoice />
        <ActiveBar />
        <Toasts />
        <PomodoroBar />
        {sync.status === 'paused' && (
          <div
            className="bg-rose-950/60 text-rose-200 text-xs px-4 py-1.5 flex justify-between items-center"
            role="status"
          >
            <span>Sync paused: the server rejected the token.</span>
            <Link to="/settings/$section" params={{ section: 'server' }} className="underline">
              Fix in Settings
            </Link>
          </div>
        )}
        {mismatch && (
          <div className="bg-amber-950/50 text-amber-200 text-xs px-4 py-1.5" role="status">
            Server day is {mismatch.server}, you're in {mismatch.local}.
          </div>
        )}
        <UpdateChip />
        {/* pages carry their own focus toggle; other views need a way out */}
        {focusMode && !path.startsWith('/pages/') && (
          <button
            type="button"
            onClick={toggleFocus}
            className="fixed top-[calc(0.5rem+env(safe-area-inset-top))] right-3 z-30 h-8 px-2.5 rounded-md bg-panel/80 backdrop-blur border border-line text-xs text-muted hover:text-fg flex items-center gap-1.5 fade-in"
            title="Exit focus (⌘.)"
            data-testid="focus-exit"
          >
            <Minimize2 size={14} aria-hidden /> Focus
          </button>
        )}
        <ScrollIndicator />
        <main
          className={cx(
            'page-vt flex-1 w-full mx-auto px-3 pt-2',
            // Home sizes itself to the screen; everything else scrolls clear of the tab bar
            path === '/home' ? 'pb-0' : 'pb-24 md:pb-8',
            // a page gets the editor column plus its side panel
            // detailed pages (a page's editor, the library's insights) get the whole width of a desk; lists stay narrow
            focusMode
              ? 'max-w-3xl'
              : docked
                ? 'max-w-none'
                : path.startsWith('/pages/') ||
                    path.startsWith('/music') ||
                    path === '/library' ||
                    path.startsWith('/agents') ||
                    path.startsWith('/dev/')
                  ? 'max-w-6xl'
                  : 'max-w-2xl',
          )}
        >
          <Outlet />
        </main>
        {/* the player follows every page while Spotify is playing; on the phone it sits above the tab bar */}
        <VoiceBar />
        {!member && <PlayerBar className="sticky z-20 bottom-[calc(var(--tabbar-top)+0.5rem)] md:bottom-0" />}

        <nav
          hidden={focusMode || keyboardOpen}
          // a floating capsule (.tabbar in index.css): five equal-footed columns with the
          // add button in the middle one, so it is centred whatever the tab labels measure
          ref={navRef}
          className="tabbar md:hidden fixed z-30 grid grid-cols-[1fr_1fr_auto_1fr_1fr] items-center px-1.5 touch-none select-none"
          aria-label="Primary"
          {...scrub}
        >
          {/* the day along the top edge, now lit on it */}
          <HorizonEdge className="absolute top-0 left-7 right-7" />
          {/* slides to whichever tab is current; placed from the DOM, see the effect above */}
          <span
            ref={pillRef}
            className="tabbar-pill absolute top-1.5 bottom-1.5 left-0 opacity-0"
            aria-hidden
          />
          {/* on the phone the dot only shows when something is wrong (green all day is noise), and it sits
              in the capsule's own left edge, never over the page */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-auto">
            <SyncDot quiet />
          </div>
          {dock.slice(0, 2).map((n, i) => (
            <BarTab
              key={n.to}
              item={n}
              active={!launcherOpen && isActive(n.to, n.exact)}
              editing={editingHome}
              onEdit={() => setDockSlot(i)}
              onTap={() => tabTap(n)}
            />
          ))}
          <CentreButton
            icon={badge}
            title={title}
            prime={devSuite}
            onTap={() => (devSuite ? void navigate({ to: '/agents' }) : setQuickAdd(true))}
            onSwitch={() => setSwitcher(true)}
            onSlidePick={(slug) => {
              setSwitcher(false)
              pickSuite(slug, (to) => void navigate({ to }))
            }}
          />
          {dock.slice(2).map((n) => (
            <BarTab
              key={n.to}
              item={n}
              active={!launcherOpen && isActive(n.to, n.exact)}
              editing={editingHome}
              onEdit={() => setDockSlot(2)}
              onTap={() => tabTap(n)}
            />
          ))}
          <button
            type="button"
            onClick={() => setLauncher(!launcherOpen)}
            aria-haspopup="dialog"
            aria-expanded={launcherOpen}
            data-nav-more=""
            className={cx(
              'relative z-10 flex flex-col items-center justify-center gap-0.5 h-full w-full min-w-12 text-[10.5px] font-medium',
              appsActive ? 'text-accent-text' : 'text-muted',
            )}
            data-active={appsActive || undefined}
            data-testid="nav-more"
          >
            <LayoutGrid size={21} strokeWidth={appsActive ? 2.2 : 1.8} aria-hidden />
            Apps
          </button>
        </nav>
        <Launcher />
        <DockPicker slot={dockSlot} layout={phone} onClose={() => setDockSlot(null)} />
        <QuickAdd />
        <GlobalMenus />
        <EmbedDrawer />
        <SuiteSwitcher
          open={switcherOpen}
          onClose={() => setSwitcher(false)}
          onInstall={() => {
            // the install sheet takes over from the switcher, so installing lands in the new suite
            setSwitcher(false)
            setInstalling(true)
          }}
        />
        <SuiteInstallSheet open={installing} onClose={() => setInstalling(false)} />
        <CommandPalette />
        <SuiteFlash />
      </div>
      {/* right rail: a page's panel lives here on desktop, on the outer surface like the sidebar */}
      {!focusMode && (
        <div
          ref={setRightRail}
          className="hidden md:block empty:hidden w-[20rem] sticky top-0 h-dvh overflow-y-auto py-3 pr-3 pl-1"
          data-testid="right-rail"
        />
      )}
    </div>
  )
}
